import { apiFetch } from "../api";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";
import { Scanner } from "@yudiel/react-qr-scanner";

import { useI18n } from "../i18n/I18nContext";

export default function ConformitePli() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();

  const [operator, setOperator]       = useState("");
  const [pns, setPns]                 = useState([]);
  const [pn, setPn]                   = useState("");
  const [pnSearch, setPnSearch]       = useState("");
  const [showPnList, setShowPnList]   = useState(false);
  const [fentes, setFentes]           = useState("");
  const [noeud, setNoeud]             = useState("");
  const [moisissure, setMoisissure]   = useState("");
  const [decoloration, setDecoloration] = useState("");
  const [longueur, setLongueur]       = useState("");
  const [largeur, setLargeur]         = useState("");
  const [humidite, setHumidite]       = useState("");
  const [epaisseur, setEpaisseur]     = useState("");
  const [autres, setAutres]           = useState("");
  const [qtyNok, setQtyNok]           = useState("");
  const [comment, setComment]         = useState("");
  const [now, setNow]                 = useState(new Date());
  const [loadingPn, setLoadingPn]     = useState(false);
  const [sending, setSending]         = useState(false);
  const [error, setError]             = useState("");
  const [success, setSuccess]         = useState("");
  const pnBoxRef                      = useRef(null);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (raw) { try { setOperator(JSON.parse(raw).username || ""); } catch (e) { console.error(e); } }
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (pnBoxRef.current && !pnBoxRef.current.contains(e.target)) setShowPnList(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    async function loadPn() {
      setLoadingPn(true);
      try {
        const res  = await apiFetch("/api/list");
        if (!res.ok) throw new Error();
        const data = await res.json();
        const allPn = Array.isArray(data) ? data.map((i) => i.PN || i.pn).filter(Boolean) : [];
        setPns(Array.from(new Set(allPn)));
      } catch { setError("Impossible de charger la liste des PN."); }
      finally { setLoadingPn(false); }
    }
    loadPn();
  }, []);

  function normalizeText(text) {
    return text.toString().toLowerCase().normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
  }

  const filteredPns = pns.filter((p) => {
    const s = normalizeText(pnSearch);
    return !s || normalizeText(p).includes(s);
  });

  function resetForm() {
    setFentes(""); setNoeud(""); setMoisissure(""); setDecoloration("");
    setLongueur(""); setLargeur(""); setHumidite(""); setEpaisseur("");
    setAutres(""); setQtyNok(""); setComment("");
  }

  async function handleSubmit(e) {
    e.preventDefault(); setError(""); setSuccess("");
    if (!pn) { setError("Merci de renseigner un PN."); return; }

    const payload = {
      operator, pn,
      qtyNok      : Number(qtyNok)      || 0,
      fentes      : Number(fentes)      || 0,
      noeud       : Number(noeud)       || 0,
      moisissure  : Number(moisissure)  || 0,
      decoloration: Number(decoloration)|| 0,
      longueur    : Number(longueur)    || 0,
      largeur     : Number(largeur)     || 0,
      humidite    : Number(humidite)    || 0,
      epaisseur   : Number(epaisseur)   || 0,
      autres      : Number(autres)      || 0,
      comment     : comment || "",
      timestamp   : new Date().toISOString(),
    };

    try {
      setSending(true);
      const res = await apiFetch("/api/production/conformite-pli", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.message || "Erreur serveur"); }
      resetForm();
      setSuccess("✅ Données enregistrées avec succès.");
    } catch (err) { setError("❌ " + (err.message || "Erreur lors de l'envoi.")); }
    finally { setSending(false); }
  }

  const timeStr = now.toLocaleTimeString("fr-FR");
  const dateStr = now.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="cp-page">

      {/* SCANNER MODAL */}
      {isScannerOpen && (
        <div className="cp-modal-backdrop">
          <div className="cp-modal-scanner">
            <div className="cp-modal-title">📷 Scanner un QR code PN</div>
            <div style={{ borderRadius: 10, overflow: "hidden" }}>
              <Scanner
                onScan={(codes) => {
                  const text = codes?.[0]?.rawValue;
                  if (!text) return;
                  setPn(text); setPnSearch(text); setShowPnList(false); setIsScannerOpen(false);
                }}
                onError={(e) => console.error("QR:", e)}
              />
            </div>
            <button className="cp-btn-close-scanner" onClick={() => setIsScannerOpen(false)}>✕ Fermer</button>
          </div>
        </div>
      )}

      <div className="cp-card">

        {/* HEADER */}
        <div className="cp-header">
          <span>Conformité Pli</span>
          <span className="cp-header-sub">Contrôle qualité des plis</span>
        </div>

        {/* NAV */}
        <div className="cp-nav">
          <button type="button" className="cp-btn-back" onClick={() => navigate("/menu")}>← Retour menu</button>
          <div className="cp-clock">
            <div className="cp-clock-time">{timeStr}</div>
            <div className="cp-clock-date">{dateStr}</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="cp-form">

          {/* OPÉRATEUR */}
          <div className="cp-operator-badge">
            <span className="cp-operator-dot" />
            {operator ? operator.toUpperCase() : "OPÉRATEUR NON CONNECTÉ"}
          </div>

          {/* PN */}
          <div className="cp-section">
            <div className="cp-section-label">🔖 Numéro de lot (PN)</div>
            <div ref={pnBoxRef} style={{ position: "relative" }}>
              <div className="cp-pn-row">
                <input
                  className="cp-pn-input"
                  value={pnSearch}
                  onChange={(e) => { setPnSearch(e.target.value); setShowPnList(true); }}
                  onFocus={() => setShowPnList(true)}
                  placeholder={loadingPn ? "Chargement…" : "Filtrer ou saisir un PN"}
                  disabled={loadingPn}
                />
                <button type="button" className="cp-scan-btn" onClick={() => setIsScannerOpen(true)} title="Scanner QR">📷</button>
              </div>

              {pn && (
                <div className="cp-pn-selected">
                  <span>✔ Sélectionné :</span>
                  <strong>{pn}</strong>
                  <button type="button" className="cp-pn-clear"
                    onClick={() => { setPn(""); setPnSearch(""); }}>✕</button>
                </div>
              )}

              {showPnList && filteredPns.length > 0 && (
                <div className="cp-dropdown" onMouseDown={(e) => e.preventDefault()}>
                  {filteredPns.map((p) => (
                    <div key={p} className="cp-dropdown-item"
                      onClick={() => { setPn(p); setPnSearch(p); setShowPnList(false); }}>
                      {p}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* DÉFAUTS */}
          <div className="cp-section cp-section-defauts">
            <div className="cp-section-label">🔍 Défauts détectés</div>

            <FieldNumber label="Fentes"                    value={fentes}      onChange={setFentes} />
            <FieldNumber label="Nœud"                      value={noeud}       onChange={setNoeud} />
            <FieldNumber label="Moisissure"                value={moisissure}  onChange={setMoisissure} />
            <FieldNumber label="Décoration / Décoloration" value={decoloration}onChange={setDecoloration} />
            <FieldNumber label="Longueur"                  value={longueur}    onChange={setLongueur} />
            <FieldNumber label="Largeur"                   value={largeur}     onChange={setLargeur} />
            <FieldNumber label="Humidité"                  value={humidite}    onChange={setHumidite} />
            <FieldNumber label="Épaisseur"                 value={epaisseur}   onChange={setEpaisseur} />
            <FieldNumber label="Autres"                    value={autres}      onChange={setAutres} />

            {/* Séparateur */}
            <div className="cp-separator" />

            <FieldNumber label="🔴 Quantité de Pli NOK" value={qtyNok} onChange={setQtyNok} highlight />
          </div>

          {/* COMMENTAIRE */}
          <div className="cp-section">
            <div className="cp-section-label">💬 Commentaires / observations</div>
            <textarea
              rows={3} value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="cp-textarea"
              placeholder="Saisir un commentaire si nécessaire…"
            />
          </div>

          {/* MESSAGES */}
          {error   && <div className="cp-msg cp-msg-error">{error}</div>}
          {success && <div className="cp-msg cp-msg-success">{success}</div>}

          {/* SUBMIT */}
          <div className="cp-submit-row">
            <button type="submit" disabled={sending} className="cp-btn-submit">
              {sending ? "⏳ Envoi en cours…" : "✔ OK confirmation & envoi"}
            </button>
            <button type="button" className="cp-btn-time">
              Time
              <div className="cp-btn-time-sub">{timeStr}</div>
            </button>
          </div>

        </form>
      </div>

      <Style />
    </div>
  );
}

/* ── FIELD NUMBER ── */
function FieldNumber({ label, value, onChange, disabled = false, highlight = false }) {
  return (
    <div className={`cp-field ${highlight ? "cp-field-highlight" : ""}`}>
      <span className="cp-field-label">{label}</span>
      <input
        type="number" min="0"
        value={value} disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.value)}
        className={`cp-field-input ${disabled ? "cp-field-disabled" : ""} ${highlight ? "cp-field-input-highlight" : ""}`}
      />
    </div>
  );
}

/* ── STYLES ── */
function Style() {
  return (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

      .cp-page {
        min-height: 100vh; width: 100%;
        background: radial-gradient(ellipse at top, #01283a 0%, #010F14 70%);
        display: flex; justify-content: center; align-items: flex-start;
        padding: 16px;
        font-family: 'Inter','Segoe UI',sans-serif;
      }

      .cp-card {
        width: 100%; max-width: 640px;
        background: #011e2b;
        border: 1px solid #1a4a5a; border-radius: 16px; overflow: hidden;
        box-shadow: 0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px #0a3040;
      }

      /* HEADER */
      .cp-header {
        background: linear-gradient(135deg, #E6FF3E 0%, #c8e800 100%);
        color: #000; padding: 14px 20px;
        display: flex; flex-direction: column; align-items: center; gap: 2px;
      }
      .cp-header span    { font-size: 15px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; }
      .cp-header-sub     { font-size: 11px; font-weight: 500; opacity: 0.6; }

      /* NAV */
      .cp-nav {
        background: #010f14; padding: 8px 16px;
        display: flex; justify-content: space-between; align-items: center;
        border-bottom: 1px solid #1a4a5a;
      }
      .cp-btn-back {
        background: transparent; color: #7ab8cc;
        border: 1px solid #1a4a5a; border-radius: 6px;
        padding: 5px 10px; font-size: 11px; font-weight: 600; cursor: pointer; transition: all 0.2s;
      }
      .cp-btn-back:hover { background: #1a4a5a; color: #fff; }
      .cp-clock          { text-align: right; }
      .cp-clock-time     { color: #E6FF3E; font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; }
      .cp-clock-date     { color: #5a8a99; font-size: 10px; }

      /* FORM */
      .cp-form { padding: 20px; display: flex; flex-direction: column; gap: 20px; overflow-y: auto; flex: 1; }

      /* OPÉRATEUR */
      .cp-operator-badge {
        display: flex; align-items: center; gap: 10px;
        background: linear-gradient(90deg, #012836, #013d50);
        border: 1px solid #1a5a6a; border-radius: 10px;
        padding: 12px 16px; color: #fff; font-size: 14px; font-weight: 700; letter-spacing: 0.06em;
      }
      .cp-operator-dot {
        width: 10px; height: 10px; background: #00B050;
        border-radius: 50%; box-shadow: 0 0 6px #00B050; flex-shrink: 0;
      }

      /* SECTION */
      .cp-section {
        background: #012030; border: 1px solid #1a4a5a;
        border-radius: 12px; padding: 14px 16px;
        display: flex; flex-direction: column; gap: 10px;
      }
      .cp-section-defauts { border-color: #2a3a5a; background: #010f1a; }
      .cp-section-label {
        font-size: 12px; font-weight: 700; text-transform: uppercase;
        letter-spacing: 0.08em; color: #7ab8cc;
      }

      /* PN */
      .cp-pn-row { display: flex; gap: 8px; align-items: center; }
      .cp-pn-input {
        flex: 1; background: #003818; color: #7aff9a;
        border: 1px solid #005020; border-radius: 8px;
        padding: 10px 14px; font-size: 14px; font-weight: 600; text-align: center;
        outline: none; transition: border 0.2s;
      }
      .cp-pn-input:focus { border-color: #00B050; box-shadow: 0 0 0 2px rgba(0,176,80,0.15); }
      .cp-scan-btn {
        width: 46px; height: 46px; background: #012030;
        border: 1px solid #1a4a5a; border-radius: 8px;
        font-size: 20px; cursor: pointer; transition: background 0.2s; flex-shrink: 0;
      }
      .cp-scan-btn:hover { background: #1a4a5a; }
      .cp-pn-selected {
        display: flex; align-items: center; gap: 8px; margin-top: 8px;
        background: #002810; border: 1px solid #00B050; border-radius: 8px;
        padding: 6px 12px; font-size: 12px; color: #7aff9a;
      }
      .cp-pn-selected strong { flex: 1; font-size: 13px; }
      .cp-pn-clear { background: transparent; border: none; color: #ff8080; cursor: pointer; font-size: 14px; font-weight: 700; }
      .cp-dropdown {
        position: absolute; left: 0; right: 0; margin-top: 4px;
        max-height: 200px; overflow-y: auto;
        background: #002818; border: 1px solid #005020; border-radius: 8px; z-index: 50;
        box-shadow: 0 8px 24px rgba(0,0,0,0.5);
      }
      .cp-dropdown-item {
        padding: 8px 14px; color: #aaffcc; font-size: 13px;
        cursor: pointer; border-bottom: 1px solid #003818; transition: background 0.15s;
      }
      .cp-dropdown-item:hover { background: #003d20; }

      /* FIELD */
      .cp-field {
        display: flex; align-items: center; justify-content: space-between; gap: 12px;
        padding: 8px 10px; border-radius: 8px; transition: background 0.15s;
      }
      .cp-field:hover        { background: rgba(255,255,255,0.03); }
      .cp-field-highlight    { background: rgba(192,0,0,0.12); border: 1px solid #5a1a1a; border-radius: 8px; padding: 10px 12px; }
      .cp-field-label        { color: #c8d8e8; font-size: 14px; font-weight: 600; flex: 1; }
      .cp-field-highlight .cp-field-label { color: #ffaaaa; font-size: 15px; }
      .cp-field-input {
        width: 120px; background: #010F14; color: #fff;
        border: 1px solid #1a4a5a; border-radius: 8px;
        padding: 8px 10px; font-size: 15px; text-align: center; font-weight: 600;
        outline: none; transition: border 0.2s; flex-shrink: 0;
      }
      .cp-field-input:focus         { border-color: #E6FF3E; box-shadow: 0 0 0 2px rgba(230,255,62,0.15); }
      .cp-field-disabled            { background: #1a1a1a; opacity: 0.6; cursor: not-allowed; }
      .cp-field-input-highlight     { border-color: #C00000; background: #1a0808; color: #ff9090; font-size: 16px; }
      .cp-field-input-highlight:focus { border-color: #ff4040; box-shadow: 0 0 0 2px rgba(192,0,0,0.2); }

      .cp-separator { border: none; border-top: 1px solid #1a4a5a; margin: 4px 0; }

      /* TEXTAREA */
      .cp-textarea {
        width: 100%; background: #1a0e06; color: #f0d0b0;
        border: 1px solid #5a3020; border-radius: 8px;
        padding: 10px 12px; font-size: 13px; resize: vertical;
        outline: none; transition: border 0.2s; font-family: inherit;
      }
      .cp-textarea:focus { border-color: #ff8040; box-shadow: 0 0 0 2px rgba(255,128,64,0.1); }

      /* MESSAGES */
      .cp-msg         { border-radius: 8px; padding: 10px 14px; font-size: 13px; font-weight: 600; }
      .cp-msg-error   { background: #2a0808; border: 1px solid #C00000; color: #ff9090; }
      .cp-msg-success { background: #002810; border: 1px solid #00B050; color: #80ff90; }

      /* SUBMIT */
      .cp-submit-row { display: flex; gap: 10px; align-items: center; }
      .cp-btn-submit {
        flex: 1; background: linear-gradient(135deg, #00B050, #008a3c);
        color: #000; border: none; border-radius: 10px;
        padding: 14px; font-size: 15px; font-weight: 800;
        text-transform: uppercase; letter-spacing: 0.08em;
        cursor: pointer; transition: opacity 0.2s, transform 0.1s;
        box-shadow: 0 4px 16px rgba(0,176,80,0.3);
      }
      .cp-btn-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
      .cp-btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }
      .cp-btn-time {
        min-width: 110px; background: #C00000; color: #fff;
        border: 2px solid #7F0000; border-radius: 8px;
        padding: 8px 10px; font-weight: 700; font-size: 12px; cursor: default; text-align: center;
      }
      .cp-btn-time-sub { font-size: 11px; margin-top: 2px; font-variant-numeric: tabular-nums; }

      /* SCANNER MODAL */
      .cp-modal-backdrop {
        position: fixed; inset: 0; background: rgba(0,0,0,0.8);
        display: flex; justify-content: center; align-items: center;
        z-index: 999; backdrop-filter: blur(4px);
      }
      .cp-modal-scanner {
        width: 90%; max-width: 420px; background: #011e2b;
        border: 1px solid #00B050; border-radius: 14px; padding: 16px;
        display: flex; flex-direction: column; gap: 12px;
        box-shadow: 0 0 40px rgba(0,176,80,0.2);
      }
      .cp-modal-title { color: #fff; font-size: 14px; font-weight: 700; text-align: center; }
      .cp-btn-close-scanner {
        background: #C00000; color: #fff; border: none; border-radius: 8px;
        padding: 10px; font-weight: 700; font-size: 13px; cursor: pointer; transition: opacity 0.2s;
      }
      .cp-btn-close-scanner:hover { opacity: 0.85; }

      /* TABLETTE */
      @media (min-width: 768px) {
        .cp-card  { max-width: 720px; }
        .cp-header span { font-size: 17px; }
        .cp-form  { padding: 24px 28px; gap: 22px; }
        .cp-field-label  { font-size: 15px; }
        .cp-field-input  { font-size: 16px; width: 140px; }
        .cp-btn-submit   { font-size: 16px; padding: 16px; }
      }

      /* DESKTOP */
      @media (min-width: 1024px) {
        .cp-card  { max-width: 900px; }
        .cp-header span  { font-size: 20px; }
        .cp-form  { padding: 28px 36px; gap: 26px; }
        .cp-operator-badge { font-size: 16px; padding: 14px 20px; }
        .cp-section      { padding: 18px 20px; }
        .cp-section-label{ font-size: 14px; }
        .cp-field-label  { font-size: 16px; }
        .cp-field-input  { font-size: 18px; width: 160px; padding: 10px 14px; }
        .cp-field-input-highlight { font-size: 20px; }
        .cp-btn-submit   { font-size: 18px; padding: 18px; }
        .cp-clock-time   { font-size: 18px; }
        .cp-pn-input     { font-size: 16px; padding: 12px 18px; }
        .cp-scan-btn     { width: 54px; height: 54px; font-size: 24px; }
      }

      /* XXL */
      @media (min-width: 1400px) {
        .cp-page  { padding: 32px; }
        .cp-card  { max-width: 1100px; border-radius: 20px; }
        .cp-header { padding: 24px 40px; }
        .cp-header span  { font-size: 26px; letter-spacing: 0.14em; }
        .cp-header-sub   { font-size: 14px; }
        .cp-nav   { padding: 12px 28px; }
        .cp-btn-back     { font-size: 13px; padding: 7px 14px; }
        .cp-clock-time   { font-size: 22px; }
        .cp-clock-date   { font-size: 12px; }
        .cp-form  { padding: 36px 48px; gap: 32px; }
        .cp-operator-badge { font-size: 20px; padding: 16px 24px; border-radius: 14px; }
        .cp-operator-dot   { width: 14px; height: 14px; }
        .cp-section { padding: 22px 26px; gap: 14px; border-radius: 16px; }
        .cp-section-label  { font-size: 15px; letter-spacing: 0.1em; }
        .cp-field { padding: 10px 14px; }
        .cp-field-label    { font-size: 20px; }
        .cp-field-input    { font-size: 22px; width: 200px; padding: 14px 18px; border-radius: 10px; }
        .cp-field-input-highlight { font-size: 24px; width: 200px; }
        .cp-field-highlight { padding: 14px 16px; }
        .cp-pn-input  { font-size: 20px; padding: 14px 20px; border-radius: 10px; height: 58px; }
        .cp-scan-btn  { width: 64px; height: 64px; font-size: 28px; border-radius: 10px; }
        .cp-pn-selected    { font-size: 14px; padding: 8px 16px; }
        .cp-pn-selected strong { font-size: 16px; }
        .cp-dropdown-item  { font-size: 15px; padding: 10px 18px; }
        .cp-textarea  { font-size: 18px; padding: 14px 16px; border-radius: 10px; min-height: 100px; }
        .cp-msg  { font-size: 15px; padding: 14px 18px; border-radius: 10px; }
        .cp-btn-submit     { font-size: 22px; padding: 22px; border-radius: 14px; letter-spacing: 0.1em; }
        .cp-btn-time       { min-width: 160px; font-size: 14px; padding: 12px; border-radius: 10px; }
        .cp-btn-time-sub   { font-size: 13px; }
      }
    `}</style>
  );
}
